/* eslint-disable no-invalid-this */
import parsePhoneNumber from 'libphonenumber-js';
import validationName from './validationName';
import * as yup from 'yup';
import YupPassword from 'yup-password';
import { getPhoneIsValid } from './getPhoneIsValid';
import { stripNumber } from './stripPhoneNumber';

YupPassword(yup);

export const createVIPAccountSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('First Name is required.')
    .min(2, 'First name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'First name must include letter characters',
      (value) => /[a-z]/gi.test(value),
    )
    .test(
      'validationType',
      'Please enter a valid first name',
      (value) => !/[0123456789!#$%^&*()+=;:~?<>[\]{}|\\@]/gi.test(value),
    ),
  lastName: yup
    .string()
    .required('Last Name is required.')
    .min(2, 'Last name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'Last name must include letter characters',
      (value) => /[a-z]/gi.test(value),
    )
    .test(
      'validationType',
      'Please enter a valid last name',
      (value) => !/[0123456789!#$%^&*()+=;:~?<>[\]{}|\\@]/gi.test(value),
    ),
  phone: yup
    .string()
    .nullable()
    .notRequired()
    .when('phone', {
      is: (value) => value?.length,
      then: yup
        .string()
        .test(
          'validateRegex',
          'Please enter a valid mobile number.',
          (value) => /^\+?\d+$/.test(value),
        )
        .min(8, 'Mobile number must contain at least 8 characters'),
    }),
  email: yup
    .string()
    .required('Email Address is required.')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
  marketingComms: yup
    .boolean()
    .required('Accepting terms and conditions is required')
    .oneOf([true], 'Accepting terms and conditions is required'),
  password: yup
    .string()
    .required('Password is required.')
    .min(8, 'Password must contain at least 8 characters')
    .minNumbers(1, 'Password must contain 1 number')
    .minSymbols(1, 'Password must contain 1 symbol')
    .minUppercase(1, 'Password must contain 1 uppercase letter'),
}, [['phone', 'phone']]);

export const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email Address is required.')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
});

export const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required('Password is required.')
    .min(8, 'Password must contain at least 8 characters')
    .minNumbers(1, 'Password must contain 1 number')
    .minSymbols(1, 'Password must contain 1 symbol')
    .minUppercase(1, 'Password must contain 1 uppercase letter'),
  confirmPassword: yup.string().oneOf([yup.ref('password'), null]),
});

export const loginSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email Address is required.')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
  password: yup
    .string()
    .required('Password is required.')
    .min(8, 'Password must contain at least 8 characters')
    .minNumbers(1, 'Password must contain 1 number')
    .minSymbols(1, 'Password must contain 1 symbol')
    .minUppercase(1, 'Password must contain 1 uppercase letter'),
});

export const giftCardSchema = yup
  .object()
  .shape({
    recipientEmail: yup
      .string()
      .required('Recipient Email Address is required.')
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
        'Please enter a valid recipient email address.',
      ),
    yourName: yup
      .string()
      .required('Your Name is required.')
      .min(2, 'Your name must contain at least 2 characters')
      .test(
        'validationCharacters',
        'Your name must include letter characters',
        (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
      )
      .test('validationType', 'Please enter a valid first name', (value) =>
        validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
      ),
    recipientName: yup
      .string()
      .required('Recipient Name is required.')
      .min(2, 'Recipient name must contain at least 2 characters')
      .test(
        'validationCharacters',
        'Recipient name must include letter characters',
        (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
      )
      .test('validationType', 'Please enter a valid last name', (value) =>
        validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
      ),
  })
  .shape(
    {
      customAmount: yup.string().when('amount', {
        is: '',
        then: yup
          .string()
          .required('Custom Amount is required')
          .test(
            'validationAmount',
            'Please enter amount more than $20 and less than $500',
            (value) => Number(value) >= 20 && Number(value) <= 500,
          ),
        otherwise: yup.string(),
      }),

      amount: yup.string().when('customAmount', {
        is: '',
        then: yup.string().required('Amount is required'),
        otherwise: yup.string(),
      }),
    },
    [['customAmount', 'amount']],
  );

export const guestAccountSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email Address is required.')
    .matches(
      // eslint-disable-next-line no-useless-escape
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
  firstName: yup
    .string()
    .required('First Name is required.')
    .min(2, 'First name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'First name must include letter characters',
      (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
    )
    .test('validationType', 'Please enter a valid first name', (value) =>
      validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
    ),
  lastName: yup
    .string()
    .required('Last Name is required.')
    .min(2, 'Last name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'Last name must include letter characters',
      (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
    )
    .test('validationType', 'Please enter a valid last name', (value) =>
      validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
    ),
  isCreatePassword: yup.boolean(),
  isMarketingComms: yup.boolean().when('isCreatePassword', {
    is: true,
    then: yup
      .boolean()
      .oneOf([true], 'Accepting terms and conditions is required'),
  }),
  password: yup.string().when('isCreatePassword', {
    is: true,
    then: yup
      .string()
      .required('Password is required.')
      .min(8, 'Password must contain at least 8 characters')
      .minNumbers(1, 'Password must contain 1 number')
      .minSymbols(1, 'Password must contain 1 symbol')
      .minUppercase(1, 'Password must contain 1 uppercase letter'),
  }),
});

export const changePasswordSchema = yup.object().shape({
  oldPassword: yup
    .string()
    .required('Current Password is required.')
    .min(8, 'Password must contain at least 8 characters')
    .minNumbers(1, 'Password must contain 1 number')
    .minSymbols(1, 'Password must contain 1 symbol')
    .minUppercase(1, 'Password must contain 1 uppercase letter'),
  newPassword: yup
    .string()
    .required('New password is required.')
    .min(8, 'Password must contain at least 8 characters')
    .minNumbers(1, 'Password must contain 1 number')
    .minSymbols(1, 'Password must contain 1 symbol')
    .minUppercase(1, 'Password must contain 1 uppercase letter'),
  confirmNewPassword: yup
    .string()
    .required('Confirm New password is required.')
    .min(8, 'Password must contain at least 8 characters')
    .test('passwords-match', 'Passwords must match', function (value) {
      if (value.length >= 8) return this.parent.newPassword === value; // eslint-disable-line no-invalid-this
    }),
});

export function personalInfoSchema(projectConfig) {
  return yup.object().shape({
    firstName: yup
      .string()
      .required('First Name is required.')
      .min(2, 'First name must contain at least 2 characters')
      .test(
        'validationCharacters',
        'First name must include letter characters',
        (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
      )
      .test('validationType', 'Please enter a valid first name', (value) =>
        validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
      ),
    lastName: yup
      .string()
      .required('Last Name is required.')
      .min(2, 'Last name must contain at least 2 characters')
      .test(
        'validationCharacters',
        'Last name must include letter characters',
        (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
      )
      .test('validationType', 'Please enter a valid last name', (value) =>
        validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
      ),
    isCreatePassword: yup.boolean(),
    password: yup.string().when('isCreatePassword', {
      is: true,
      then: yup
        .string()
        .required('Password is required.')
        .min(8, 'Password must contain at least 8 characters')
        .minNumbers(1, 'Password must contain 1 number')
        .minSymbols(1, 'Password must contain 1 symbol')
        .minUppercase(1, 'Password must contain 1 uppercase letter'),
    }),
    email: yup
      .string()
      .required('Email Address is required.')
      .matches(
        /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,9})+$/,
        'Please enter a valid email address.',
      ),
    phone: yup
      .string()
      .required('Mobile number is required.')
      .min(8, 'Mobile number must contain at least 8 characters')
      .test(
        'validateRegex',
        'Please enter a valid mobile number.',
        function (value) {
          const strippedValue = value.replace(/\s+/g, '');

          if (strippedValue === null || strippedValue.indexOf(' ') >= 0) {
            return false;
          }
          const country = this.parent.country;
          const phoneNumber = parsePhoneNumber(strippedValue, country);
          if (!phoneNumber) return;
          const { countryCallingCode } = phoneNumber || {};
          return !!stripNumber(strippedValue, countryCallingCode).match(
            /^(\+?\(61\)|\(\+?61\)|\+?61|\(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$/g,
          ) ||
          !!stripNumber(strippedValue, countryCallingCode).match(
            /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s.]{0,1}[0-9]{3}[-\s.]{0,1}[0-9]{4}$/g,
          );
        },
      )
      .test('validationPhone', 'Invalid phone number', function (value) {
        const strippedValue = value.replace(/\s+/g, '');
        const country = this.parent.country;
        const initialNumber = this.options.context.type;
        const isPhoneValid = this.options.context.validNumber;
        const setIsPhoneValid = this.options.context.setValidNumber;
        const requestKey = this.options.context.requestKey;

        const { nationalNumber: initialNationalNumber } = initialNumber
          ? parsePhoneNumber(initialNumber, country)
          : {};

        const phoneNumber = parsePhoneNumber(strippedValue, country);

        if (!phoneNumber) return;

        const {
          countryCallingCode,
          number,
          country: phoneCountry,
        } = phoneNumber || {};

        const strippedInitialNumber =
          initialNationalNumber &&
          stripNumber(initialNationalNumber, countryCallingCode);

        const strippedNumber = stripNumber(strippedValue, countryCallingCode);

        if (phoneCountry !== country) {
          setIsPhoneValid(false);
          return false;
        }

        if (strippedNumber !== strippedInitialNumber) {
          return new Promise((resolve, reject) => {
            getPhoneIsValid(
              strippedNumber,
              number,
              country,
              projectConfig,
              this.options.context.setIsValidationLoading,
              resolve,
              countryCallingCode,
              setIsPhoneValid,
              requestKey,
            );
          });

          // value,
          //   number,
          //   country,
          //   projectConfig,
          //   setValidate,
          //   resolve,
          //   countryCallingCode,
          //   setIsPhoneValid,
          //   requestKey,
        }

        if (
          isPhoneValid &&
          strippedNumber !== '' &&
          strippedInitialNumber !== '' &&
          strippedNumber === strippedInitialNumber
        ) {
          return true;
        }
      }),
    country: yup
      .string()
      .trim()
      .required('Country is required.')
      .min(2, 'Country must contain at least 2 characters'),
  });
}

export function getAddressValidationRules(countriesWithState, projectConfig) {
  return yup.object().shape({
    streetName: yup
      .string()
      .trim()
      .required('Address is required.')
      .min(2, 'Address must contain at least 2 characters')
      .max(50, 'Address must not exceed 50 characters.')
      .test(
        'chosenAddress',
        'Please select an address from the autosuggestion',
        function () {
          const { isManualInput, country } = this.parent;
          if ((country !== 'NZ' && country !== 'AU') || isManualInput)
            return true;
          return this.options.context.chosenAddress;
        },
      ),
    firstName: yup
      .string()
      .trim()
      .required('First Name is required.')
      .min(2, 'First name must contain at least 2 characters')
      .test(
        'validationCharacters',
        'First name must include letter characters 3',
        (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
      )
      .test('validationType', 'Please enter a valid first name', (value) =>
        validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
      ),
    lastName: yup
      .string()
      .trim()
      .required('Last Name is required.')
      .min(2, 'Last name must contain at least 2 characters')
      .test(
        'validationCharacters',
        'Last name must include letter characters',
        (value) => !!value.match(/[a-zA-Z]/g) && !!value.match(/[a-zA-Z]/g),
      )
      .test('validationType', 'Please enter a valid last name', (value) =>
        validationName('0123456789!#$%^&*()+=;:~?<>[]{}|\\@', value),
      ),
    city: yup.string().when('country', (country, schema) => {
      return country !== 'AU'
        ? yup
            .string()
            .trim()
            .required('City is required.')
            .min(2, 'City must contain at least 2 characters')
        : schema;
    }),
    state: yup
      .string()
      .trim()
      .when('country', (country, schema) => {
        return countriesWithState.includes(country)
          ? yup.string().required('State is required.')
          : schema;
      })
      .min(2, 'State must contain at least 2 characters'),
    postalCode: yup
      .string()
      .trim()
      .required('Post Code is required.')
      .min(2, 'Post Code must contain at least 2 characters'),
    country: yup
      .string()
      .trim()
      .required('Country is required.')
      .min(2, 'Country must contain at least 2 characters'),
    region: yup.string().when('country', (country, schema) => {
      return country === 'AU'
        ? yup
            .string()
            .trim()
            .required('Suburb is required.')
            .min(2, 'Suburb must contain at least 2 characters')
        : schema;
    }),
    phone: yup
      .string()
      .required('Mobile number is required.')
      .min(8, 'Mobile number must contain at least 8 characters')
      .test(
        'validateRegex',
        'Please enter a valid mobile number.',
        function (value) {
          const strippedValue = value.replace(/\s+/g, '');

          if (strippedValue === null || strippedValue.indexOf(' ') >= 0) {
            return false;
          }

          const country = this.parent.country;
          const phoneNumber = parsePhoneNumber(strippedValue, country);

          if (!phoneNumber) return;
          const { countryCallingCode } = phoneNumber || {};

          return !!stripNumber(strippedValue, countryCallingCode).match(
            /^(\+?\(61\)|\(\+?61\)|\+?61|\(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$/g,
          ) ||
          !!stripNumber(strippedValue, countryCallingCode).match(
            /^[(]{0,1}[0-9]{3}[)]{0,1}[-\s.]{0,1}[0-9]{3}[-\s.]{0,1}[0-9]{4}$/g,
          );
        },
      )
      .test('validationPhone', 'Invalid phone number', function (value) {
        const strippedValue = value.replace(/\s+/g, '');
        const country = this.parent.country;
        const initialNumber = this.options.context.type;
        const isPhoneValid = this.options.context.validNumber;
        const setIsPhoneValid = this.options.context.setValidNumber;
        const requestKey = this.options.context.requestKey;

        const { nationalNumber: initialNationalNumber } = initialNumber
          ? parsePhoneNumber(initialNumber, country)
          : {};

        const phoneNumber = parsePhoneNumber(strippedValue, country);

        if (!phoneNumber) return;

        const {
          countryCallingCode,
          number,
          country: phoneCountry,
        } = phoneNumber || {};

        const strippedInitialNumber =
          initialNationalNumber &&
          stripNumber(initialNationalNumber, countryCallingCode);

        const strippedNumber = stripNumber(strippedValue, countryCallingCode);

        if (phoneCountry !== country) {
          setIsPhoneValid(false);
          return false;
        }

        if (strippedNumber !== strippedInitialNumber) {
          return new Promise((resolve, reject) => {
            getPhoneIsValid(
              strippedNumber,
              number,
              country,
              projectConfig,
              this.options.context.setIsValidationLoading,
              resolve,
              countryCallingCode,
              setIsPhoneValid,
              requestKey,
            );
          });
        }

        if (
          isPhoneValid &&
          strippedNumber !== '' &&
          strippedInitialNumber !== '' &&
          strippedNumber === strippedInitialNumber
        ) {
          return true;
        }
      }),
  });
}

export const creditCardForm = yup.object().shape({
  expirydate: yup
    .string()
    .required('Expiry Date is required.')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,9})+$/,
      'Please enter a valid expiry date.',
    ),
  code: yup
    .string()
    .required('Card code is required.')
    .min(3, 'Card Number must contain at least 3 numbers')
    .max(3, 'Card Number must include only 3 numbers')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,9})+$/,
      'Please enter a valid card code.',
    ),
  cardname: yup
    .string()
    .required('Card Number is required.')
    .min(16, 'Card Number must contain 16 numbers')
    .max(16, 'Card Number must contain only 16 numbers')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,9})+$/,
      'Please enter a valid card name.',
    ),
});

export const addGiftCardFormSchema = yup.object().shape({
  voucherCode: yup
    .string()
    .required('Gift card number is required.')
    .matches(/^\d+$/, 'Gift card should have only number'),
  voucherPin: yup
    .string()
    .required('Pin number is required.')
    .matches(/^\d+$/, 'Pin should have only number'),
});

export const sharingByEmail = yup.object().shape({
  recipientEmail: yup
    .string()
    .required('Email Address is required.')
    .matches(
      // eslint-disable-next-line no-useless-escape
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
  anotherRecipientEmail: yup.string().matches(
    // eslint-disable-next-line no-useless-escape
    /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
    'Please enter a valid email address.',
  ),
});

export const validateNewLetterSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email Address is required.')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
  firstName: yup
    .string()
    .required('First Name is required.')
    .min(2, 'First name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'First name must include letter characters',
      (value) => /[a-z]/gi.test(value),
    )
    .test(
      'validationType',
      'Please enter a valid first name',
      (value) => !/[0123456789!#$%^&*()+=;:~?<>[\]{}|\\@]/gi.test(value),
    ),
  lastName: yup
    .string()
    .required('Last Name is required.')
    .min(2, 'Last name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'Last name must include letter characters',
      (value) => /[a-z]/gi.test(value),
    )
    .test(
      'validationType',
      'Please enter a valid last name',
      (value) => !/[0123456789!#$%^&*()+=;:~?<>[\]{}|\\@]/gi.test(value),
    ),
});

export const validateEmailSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email Address is required.')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
});

export const validateEmptyFieldSchema = yup.object().shape({
  postCode: yup.string().required('The field cannot be empty.'),
});

export const contactUsFormSchema = yup.object().shape({
  // eslint-disable-next-line no-useless-escape
  email: yup
    .string()
    .required('Email Address is required.')
    .matches(
      /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,12})+$/,
      'Please enter a valid email address.',
    ),
  firstName: yup
    .string()
    .required('First Name is required.')
    .min(2, 'First name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'First name must include letter characters',
      (value) => /[a-z]/gi.test(value),
    )
    .test(
      'validationType',
      'Please enter a valid first name',
      (value) => !/[0123456789!#$%^&*()+=;:~?<>[\]{}|\\@]/gi.test(value),
    ),
  lastName: yup
    .string()
    .required('Last Name is required.')
    .min(2, 'Last name must contain at least 2 characters')
    .test(
      'validationCharacters',
      'Last name must include letter characters',
      (value) => /[a-z]/gi.test(value),
    )
    .test(
      'validationType',
      'Please enter a valid last name',
      (value) => !/[0123456789!#$%^&*()+=;:~?<>[\]{}|\\@]/gi.test(value),
    ),
});
